/**
 * Sweep bar readings (appendix A §A3). The bar is the only permanent ornament on
 * the site and it earns the space by carrying real state: the crawl pipeline's
 * heartbeat stays on screen.
 *
 * Segments are built here so the server-rendered bar and the hydrated bar always
 * read the same. Time values come from the server; nothing is derived from the
 * client clock.
 */

export type SweepReading = {
  jobs: number
  companies: number
  lastSweepAt?: string | null
  nextSweepAt?: string | null
  failingSources?: number
}

/** Compact age, e.g. `11h`, `3d`, or `—` when a source has never reported. */
export function sweepAge(from: string | null | undefined, now: number) {
  if (!from) return '—'
  const parsed = Date.parse(from)
  if (Number.isNaN(parsed)) return '—'
  const hours = Math.max(0, Math.floor((now - parsed) / 3_600_000))
  if (hours < 1) return '<1h'
  if (hours < 48) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

/** Compact countdown to the next scheduled sweep. */
export function sweepCountdown(until: string | null | undefined, now: number) {
  if (!until) return '—'
  const parsed = Date.parse(until)
  if (Number.isNaN(parsed)) return '—'
  const hours = Math.max(0, Math.round((parsed - now) / 3_600_000))
  return hours < 1 ? '<1h' : `${hours}h`
}

export function sweepOk(reading: SweepReading) {
  return Number(reading.failingSources || 0) === 0 && Boolean(reading.lastSweepAt)
}

/**
 * The full desktop reading, in order.
 *
 * The bar used to lead with `SWEEP OK` / `SWEEP DEGRADED`. That was honest about
 * an internal pipeline and unreadable as anything else: a candidate deciding
 * whether to trust the index, and a crawler assessing page quality, both meet
 * the word DEGRADED before they meet a single role. The freshness of the index
 * is the fact that belongs to a reader, so that is what the bar states — and the
 * pipeline's own health moved to `/status`, where it is still published in full.
 */
export function sweepSegments(reading: SweepReading, now: number) {
  return [
    `LAST UPDATED ${sweepAge(reading.lastSweepAt, now)}`,
    `${reading.jobs} roles`,
    `${reading.companies} companies`,
    `next check ${sweepCountdown(reading.nextSweepAt, now)}`,
  ]
}

/** 375px keeps the two facts that change (appendix A §A9.2). */
export function sweepMobileSegments(reading: SweepReading, now: number) {
  return [`${reading.jobs} roles`, `updated ${sweepAge(reading.lastSweepAt, now)}`]
}

/** Next occurrence of the twice-daily crawl cron: 00:00 and 12:00 UTC. */
export function nextSweepAfter(now: Date) {
  const next = new Date(now)
  next.setUTCMinutes(0, 0, 0)
  next.setUTCHours(now.getUTCHours() < 12 ? 12 : 24)
  return next.toISOString()
}
